import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md';
}

/**
 * docs/DESIGN_SYSTEM.md §4.9 — centered on desktop, slides up from the
 * bottom on mobile (§5's "modals go full-screen / slide up" rule below
 * 768px). No backdrop blur, per the design system's explicit anti-
 * glassmorphism rule.
 */
export function Modal({ open, onClose, title, children, footer, size = 'sm' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-bg-base/70"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={[
          'relative w-full bg-bg-elevated border border-border-subtle shadow-md',
          'rounded-t-lg md:rounded-lg',
          size === 'sm' ? 'md:max-w-[480px]' : 'md:max-w-[640px]',
          'max-h-[85vh] overflow-y-auto',
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle sticky top-0 bg-bg-elevated">
          <h2 className="text-h3 text-text-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-11 w-11 flex items-center justify-center text-text-secondary hover:text-text-primary"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer ? (
          <div className="px-5 py-4 border-t border-border-subtle flex justify-end gap-2">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}
